import axios from 'axios';
import React, { useCallback, useState } from 'react';
import ReactModal from 'react-modal';
import Recaptcha from 'react-recaptcha';
import PropTypes from 'prop-types';
import Input from './Input';
import { modals } from '../../utils/contentProvider';
import { RECAPTCHA_SITE_KEY } from '../../utils/global';

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  formType: PropTypes.string.isRequired,
};

export default function Form({ open, handleClose, formType }) {
  const [error, setError] = useState('');
  const [recaptchaResponse, setRecaptchaResponse] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const formDetails = modals[formType];
  const quantQuestFormTypes = ['sponsorAnEvent', 'contactUs', 'partnerWithUs'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (recaptchaResponse === null) {
      setError('Recaptcha is required');
      return;
    }
    setError('');
    setIsSending(true);
    const data = {
      message: e.target?.message?.value,
      phoneNumber: e.target?.phone?.value,
      email: e.target?.email?.value,
      location: e.target?.location?.value,
      company: e.target?.company?.value,
    };

    if (formType === 'generalQuery') {
      data.name =
        `${e.target?.fname?.value || ''} ${e.target?.lname?.value || ''}`.trim();
    } else {
      data.fname = e.target?.fname?.value;
      data.lname = e.target?.lname?.value;
    }

    axios
      .post('https://api.algobulls.com/v3/contact', {
        formType,
        captcha: recaptchaResponse,
        ...data,
      })
      .then((data) => {
        setIsSubmitted(data?.data?.message ?? true);
      })
      .catch((err) => {
        setError(err?.response?.data?.details ?? err.toString());
      });
  };

  const verifyRecaptchaCallback = useCallback((payload) => {
    setRecaptchaResponse(payload);
  }, []);

  return (
    <ReactModal
      onAfterClose={() => {
        setError('');
        setIsSending(false);
        setIsSubmitted(false);
      }}
      isOpen={open}
      overlayClassName="form-overlay"
      className="form-modal-content">
      <button
        onClick={handleClose}
        className="modal-cross"
        type="button"
        aria-label="cross">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none">
          <path
            d="M8.98924 8.5L13.9638 2.7411C14.0472 2.6454 13.9771 2.5 13.8482 2.5H12.336C12.2469 2.5 12.1616 2.53865 12.1029 2.60491L8 7.35521L3.89714 2.60491C3.84028 2.53865 3.755 2.5 3.66404 2.5H2.15176C2.02289 2.5 1.95277 2.6454 2.03616 2.7411L7.01076 8.5L2.03616 14.2589C2.01748 14.2802 2.0055 14.3063 2.00163 14.3341C1.99777 14.3618 2.00218 14.3901 2.01435 14.4154C2.02652 14.4408 2.04594 14.4623 2.0703 14.4773C2.09465 14.4923 2.12293 14.5002 2.15176 14.5H3.66404C3.75311 14.5 3.83839 14.4613 3.89714 14.3951L8 9.64478L12.1029 14.3951C12.1597 14.4613 12.245 14.5 12.336 14.5H13.8482C13.9771 14.5 14.0472 14.3546 13.9638 14.2589L8.98924 8.5Z"
            fill="#999999"
          />
        </svg>
      </button>
      {isSubmitted ? (
        <div className="success-screen">
          <img src="/img/circle-check-big.svg" alt="check" />
          <h3>Thank You</h3>
          <p>
            {typeof isSubmitted === 'string'
              ? isSubmitted
              : 'We will get back to you very soon'}
          </p>
          <button
            className="button-unstyled"
            type="button"
            onClick={handleClose}>
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-header-wrapper">
            <h3>{formDetails?.title}</h3>
            {formDetails?.subtitle && <p>{formDetails?.subtitle}</p>}
          </div>
          {formDetails?.alert && (
            <p className="form-alert">{formDetails?.alert}</p>
          )}
          <div className="form-input-wrapper">
            <Input
              id="fname"
              label="First Name"
              type="text"
              required
              error="Please enter your name"
            />
            <Input id="lname" label="Last Name" type="text" />
            <Input
              id="phone"
              error="Please enter your phone number"
              required
              label="Phone Number"
              type="tel"
            />
            <Input
              id="email"
              label="Professional Email Address"
              type="email"
              required
              error="Please enter a valid email address"
            />
            {!quantQuestFormTypes.includes(formType) &&
              formType !== 'generalQuery' && (
                <>
                  <Input
                    id="company"
                    label="Company"
                    type="text"
                    required
                    error="Please enter your company name"
                  />
                  <Input
                    id="location"
                    error="Please enter your location"
                    required
                    label="Location"
                    type="text"
                  />
                </>
              )}

            <Input
              id="message"
              label={
                quantQuestFormTypes.includes(formType)
                  ? 'Share Your Details'
                  : 'How can we help you?'
              }
              type="textarea"
              required
              error="This field is compulsory"
              placeholder={
                quantQuestFormTypes.includes(formType)
                  ? 'Provide relevant details to help us assist you effectively.'
                  : 'Tell us about your use case and your requirements'
              }
            />
          </div>
          {formType === 'partnerWithUs' && (
            <div className="checkbox-form">
              <Input
                id="partnerWithUs"
                type="checkbox"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                label="I agree to provide ₹1.5L or USD 2000 to AlgoBulls to host Quant Quest at our institution."
                error="Please tick the checkbox to continue"
                required
              />
            </div>
          )}
          {formType === 'sponsorAnEvent' && (
            <div className="checkbox-form">
              <Input
                id="sponsorAnEvent"
                type="checkbox"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                label="I agree to sponsor a Quant Quest with a minimum of ₹1.5L or USD 2000 or more depending on the scale of the competition."
                required
                error="Please tick the checkbox to continue"
              />
            </div>
          )}
          <div className="python-form-recaptcha">
            <Recaptcha
              sitekey={RECAPTCHA_SITE_KEY}
              verifyCallback={verifyRecaptchaCallback}
            />
          </div>
          <div className="form-bnt-container">
            <button
              onClick={handleClose}
              className="button button-border"
              type="button">
              Cancel
            </button>
            <button className="button" disabled={isSending} type="submit">
              {isSending ? 'Submitting' : 'Submit'}
            </button>
          </div>
          {error && (
            <p className="form-alert error-alert">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none">
                <g clipPath="url(#clip0_5270_161510)">
                  <path
                    d="M11.1246 3.14219L17.9575 15.007C18.4364 15.843 17.8176 16.875 16.8317 16.875H3.16605C2.18011 16.875 1.56136 15.843 2.04026 15.007L8.87308 3.14219C9.36526 2.28594 10.6325 2.28594 11.1246 3.14219Z"
                    fill="#DB0000"
                  />
                  <path
                    d="M11.1246 3.14219L17.9575 15.007C18.4364 15.843 17.8176 16.875 16.8317 16.875H3.16605C2.18011 16.875 1.56136 15.843 2.04026 15.007L8.87308 3.14219C9.36526 2.28594 10.6325 2.28594 11.1246 3.14219Z"
                    stroke="#DB0000"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 11.25V8.125"
                    stroke="white"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 15C10.5178 15 10.9375 14.5803 10.9375 14.0625C10.9375 13.5447 10.5178 13.125 10 13.125C9.48223 13.125 9.0625 13.5447 9.0625 14.0625C9.0625 14.5803 9.48223 15 10 15Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_5270_161510">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>{' '}
              {error}
            </p>
          )}
        </form>
      )}
    </ReactModal>
  );
}
